import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import {
  useGetClaudeStatusQuery,
  useConnectClaudeMutation,
  useDisconnectClaudeMutation,
  useSendClaudeChatMutation,
} from "../../store/api/claudeApi";
import LoadingState from "../../components/LoadingState";

const MCP_URL = `${window.location.origin}/mcp`;

// Zero-setup alternative to pasting an API key: QC Bot is also reachable as
// a custom connector directly inside claude.ai / Claude Desktop, where
// sign-in is just the user's existing QC Bot login, not an API key. Shown
// as a collapsible aside on both the connect screen and the chat itself,
// since some people will prefer it over sharing a key with us at all.
const ClaudeAiConnectorInfo = ({ defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(MCP_URL);
      toast.success("Connector URL copied");
    } catch {
      toast.error("Couldn't copy automatically - copy the URL manually");
    }
  };

  return (
    <div className="card mt-3">
      <div className="card-body">
        <button
          type="button"
          className="btn btn-link p-0 text-decoration-none d-flex align-items-center justify-content-between w-100"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="text-secondary">
            <i className="bi bi-box-arrow-up-right me-2"></i>
            Prefer to chat in Claude.ai or Claude Desktop instead?
          </span>
          <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-down"} text-secondary`}></i>
        </button>
        {open && (
          <div className="mt-3 text-start">
            <p className="text-secondary" style={{ fontSize: "0.9rem" }}>
              No API key needed - add QC Bot as a custom connector and ask questions right inside
              Claude's own app, signed in with your existing QC Bot login.
            </p>
            <ol className="text-secondary ps-3" style={{ fontSize: "0.9rem" }}>
              <li>In claude.ai or Claude Desktop, go to Settings &rarr; Connectors &rarr; Add custom connector</li>
              <li>
                Enter this URL:
                <div className="input-group input-group-sm mt-1">
                  <input type="text" className="form-control" value={MCP_URL} readOnly />
                  <button className="btn btn-outline-secondary" type="button" onClick={handleCopyUrl}>
                    <i className="bi bi-clipboard"></i>
                  </button>
                </div>
              </li>
              <li className="mt-1">Sign in with your QC Bot email and password when prompted</li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
};

const AskClaude = () => {
  const { data: status, isLoading: isStatusLoading } = useGetClaudeStatusQuery();
  const [connectClaude, { isLoading: isConnecting }] = useConnectClaudeMutation();
  const [disconnectClaude, { isLoading: isDisconnecting }] = useDisconnectClaudeMutation();
  const [sendChat, { isLoading: isSending }] = useSendClaudeChatMutation();

  const [apiKeyInput, setApiKeyInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [showConnectorInfo, setShowConnectorInfo] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isSending]);

  const handleConnect = async (e) => {
    e.preventDefault();
    const key = apiKeyInput.trim();
    if (!key) return;
    try {
      await connectClaude(key).unwrap();
      setApiKeyInput("");
      toast.success("Connected to Claude");
    } catch (error) {
      toast.error(error.data?.message || "Failed to connect");
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect your Claude account? You'll need to reconnect to use Ask Claude again.")) {
      return;
    }
    try {
      await disconnectClaude().unwrap();
      setMessages([]);
      toast.success("Disconnected from Claude");
    } catch (error) {
      toast.error(error.data?.message || "Failed to disconnect");
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || isSending) return;

    const history = messages.map(({ role, content }) => ({ role, content }));
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setDraft("");

    try {
      const result = await sendChat({ message: text, history }).unwrap();
      setMessages((prev) => [...prev, { role: "assistant", content: result.reply }]);
    } catch (error) {
      const message = error.data?.message || "Something went wrong talking to Claude.";
      setMessages((prev) => [...prev, { role: "assistant", content: message, isError: true }]);
      toast.error(message);
    }
  };

  if (isStatusLoading) {
    return (
      <div className="p-4">
        <LoadingState message="Checking your Claude connection..." />
      </div>
    );
  }

  if (!status?.connected) {
    return (
      <div className="p-4 d-flex flex-column align-items-center">
        <div className="card" style={{ maxWidth: "480px", width: "100%" }}>
          <div className="card-body text-center py-5">
            <i className="bi bi-stars" style={{ fontSize: "2.5rem", color: "var(--accent)" }}></i>
            <h4 className="text-white mt-3 mb-2">Connect Claude</h4>
            <p className="text-secondary mb-4">
              Ask questions about your call data in plain English and get answers straight from your QC
              records. Connect your own Anthropic API key - it's stored encrypted and used only for your
              own questions.
            </p>
            <form onSubmit={handleConnect}>
              <div className="mb-3 text-start">
                <label className="form-label text-secondary">Anthropic API key</label>
                <input
                  type="password"
                  className="form-control"
                  placeholder="sk-ant-..."
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  disabled={isConnecting}
                  autoFocus
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={isConnecting || !apiKeyInput.trim()}
              >
                {isConnecting ? "Connecting..." : "Connect"}
              </button>
            </form>
            <p className="text-muted mt-3 mb-0" style={{ fontSize: "0.85rem" }}>
              Don't have a key? Get one at{" "}
              <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer">
                console.anthropic.com
              </a>
            </p>
          </div>
        </div>
        <div style={{ maxWidth: "480px", width: "100%" }}>
          <ClaudeAiConnectorInfo />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 d-flex flex-column" style={{ height: "calc(100vh - 100px)" }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="text-white mb-0">Ask Claude</h2>
        <div className="d-flex gap-2">
          <button
            className="btn btn-link btn-sm text-secondary text-decoration-none"
            onClick={() => setShowConnectorInfo((v) => !v)}
          >
            <i className="bi bi-box-arrow-up-right me-1"></i>Use Claude.ai instead
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={handleDisconnect}
            disabled={isDisconnecting}
          >
            <i className="bi bi-plug me-1"></i>Disconnect
          </button>
        </div>
      </div>

      {showConnectorInfo && <ClaudeAiConnectorInfo defaultOpen />}

      <div className="card flex-grow-1 d-flex flex-column mt-3" style={{ minHeight: 0 }}>
        <div className="card-body flex-grow-1 overflow-auto d-flex flex-column gap-3">
          {messages.length === 0 ? (
            <div className="text-center text-secondary my-auto">
              <i className="bi bi-stars d-block mb-2" style={{ fontSize: "2rem" }}></i>
              Ask about call volume, dispositions, conversion rates, or a specific caller.
            </div>
          ) : (
            messages.map((m, i) => (
              <div
                key={i}
                className={`d-flex ${m.role === "user" ? "justify-content-end" : "justify-content-start"}`}
              >
                <div
                  className="p-3 rounded-3"
                  style={{
                    maxWidth: "75%",
                    whiteSpace: "pre-wrap",
                    backgroundColor: m.isError
                      ? "var(--danger-soft)"
                      : m.role === "user"
                      ? "var(--accent)"
                      : "var(--bg-elevated)",
                    color: m.isError
                      ? "var(--danger)"
                      : m.role === "user"
                      ? "var(--accent-contrast)"
                      : "var(--text-primary)",
                  }}
                >
                  {m.content}
                </div>
              </div>
            ))
          )}
          {isSending && (
            <div className="d-flex justify-content-start">
              <div
                className="p-3 rounded-3"
                style={{ backgroundColor: "var(--bg-elevated)", color: "var(--text-secondary)" }}
              >
                <i className="bi bi-three-dots"></i> Thinking...
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <form
          onSubmit={handleSend}
          className="border-top p-3 d-flex gap-2"
          style={{ borderColor: "var(--border)" }}
        >
          <input
            type="text"
            className="form-control"
            placeholder="Ask about your call data..."
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={isSending}
          />
          <button type="submit" className="btn btn-primary" disabled={isSending || !draft.trim()}>
            <i className="bi bi-send"></i>
          </button>
        </form>
      </div>
    </div>
  );
};

export default AskClaude;
